'use client';

import { useEffect, useState } from 'react';
import classes from './RichBlocks.module.scss';
import { IconRefresh, IconRosetteDiscountCheck, IconSchool } from '@tabler/icons-react';
import { Button, Progress, Text } from '@mantine/core';
import type { QuizQuestion } from '@/src/types/RichBlock';
import { useRichBlocks } from './RichBlocksContext';

export function QuizBlock({
  title,
  questions,
  passingScore = 70,
}: {
  title: string;
  questions: QuizQuestion[];
  passingScore?: number;
}) {
  const { setQuizPassed } = useRichBlocks();
  const [answers, setAnswers] = useState<Record<number, number | null>>({});
  const [showResults, setShowResults] = useState(false);

  const safeQuestions = questions.slice(0, 20);
  const answeredCount = Object.values(answers).filter((v) => v !== null && v !== undefined).length;
  const correctCount = safeQuestions.reduce(
    (acc, q, i) => acc + (answers[i] === q.correctIndex ? 1 : 0),
    0
  );
  const score =
    safeQuestions.length > 0 ? Math.round((correctCount / safeQuestions.length) * 100) : 0;
  const passed = score >= passingScore;

  useEffect(() => {
    if (showResults && passed) {
      setQuizPassed(true);
    }
  }, [showResults, passed, setQuizPassed]);

  const handleAnswer = (questionIndex: number, choiceIndex: number) => {
    if (showResults) return;
    setAnswers((prev) => ({ ...prev, [questionIndex]: choiceIndex }));
  };

  const reset = () => {
    setAnswers({});
    setShowResults(false);
  };

  return (
    <div className={classes.quizBlock}>
      <div className={classes.quizHeader}>
        <span className={classes.quizIcon} aria-hidden="true">
          <IconSchool size={28} />
        </span>
        <div>
          <Text fw={600} size="lg" className={classes.quizTitle}>
            {title}
          </Text>
          <Text size="sm" c="dimmed">
            Score minimum : {passingScore}%
          </Text>
        </div>
      </div>
      <Progress
        value={(answeredCount / safeQuestions.length) * 100}
        color={passed ? '#10b981' : undefined}
        className={classes.quizProgress}
      />

      {safeQuestions.map((q, qi) => {
        const selected = answers[qi];
        const isAnswered = selected !== undefined && selected !== null;
        return (
          <div key={qi} className={classes.quizQuestionWrap}>
            <Text className={classes.quizQuestion}>
              <span className={classes.quizQuestionNumber}>{qi + 1}</span>
              {q.question}
            </Text>
            <div className={classes.quizChoices}>
              {q.choices.map((choice, ci) => {
                let stateClass = '';
                let selectedClass = '';
                if (showResults) {
                  if (ci === q.correctIndex) stateClass = classes.correct;
                  else if (ci === selected) stateClass = classes.incorrect;
                } else if (ci === selected) {
                  selectedClass = classes.selected;
                }
                return (
                  <button
                    key={ci}
                    type="button"
                    className={`${classes.quizChoice} ${stateClass} ${selectedClass}`}
                    onClick={() => handleAnswer(qi, ci)}
                    disabled={showResults}
                  >
                    <span className={classes.quizChoiceLetter}>{String.fromCharCode(65 + ci)}</span>
                    {choice}
                  </button>
                );
              })}
            </div>
            {showResults && isAnswered && (
              <div className={classes.quizExplanation}>
                {selected === q.correctIndex ? (
                  <span className={classes.quizExplanationCorrect}>Bonne réponse —</span>
                ) : (
                  <span className={classes.quizExplanationIncorrect}>Pas tout à fait —</span>
                )}{' '}
                {q.explanation}
              </div>
            )}
          </div>
        );
      })}

      {answeredCount === safeQuestions.length && !showResults && (
        <Button
          onClick={() => setShowResults(true)}
          fullWidth
          className={classes.quizSubmit}
          leftSection={<IconRosetteDiscountCheck size={18} />}
        >
          Voir mon score
        </Button>
      )}

      {showResults && (
        <div className={classes.quizResults}>
          <div
            className={`${classes.quizScoreCard} ${passed ? classes.quizScorePassed : classes.quizScoreFailed}`}
          >
            <Text fw={700} size="xl">
              {score}%
            </Text>
            <Text size="sm">
              {passed
                ? 'Bravo, vous avez validé le quiz.'
                : 'À réviser pour débloquer le certificat.'}
            </Text>
          </div>
          <Button variant="light" onClick={reset} leftSection={<IconRefresh size={18} />}>
            Recommencer
          </Button>
        </div>
      )}
    </div>
  );
}
